// Draws the farm's objects (spec 2026-09-29-farm-4a-objects-design.md) inside FarmWorld's
// depth-sorted `objects` container: one image per object, anchored bottom-centre on its FRONT
// cell like the crops, fences as a post plus a rail towards each neighbouring fence, a Złota
// tabliczka with its crop's picture on the board. Real art (farm_obj_<id>, authored 2×) when
// loaded, else the kind's stand-in; a diff by placement id keeps redraws cheap.
import { cellCentre, cellFToLocal, CROP_BASE_Y, TILE_W, TILE_H, depthAt } from './iso.mjs';
import { drawList, plaqueEmblem, objectAnchor, fenceSegments, fencePostNeeded, floatParts } from './farmObjects.mjs';
import { anchorFor } from './sceneryAnchors.mjs';
import { preloader } from '../scenes/PreloadScene.js';
import { iconKey } from '../ui/icons.mjs';
import { serverNow } from '../state/serverClock.mjs';

// Stand-ins live under their own keys (farmPlaceholders.js), so a real texture can always load later.
const PH = { tree: 'farm_obj_ph_tree', decoration: 'farm_obj_ph_decoration', landmark: 'farm_obj_ph_landmark', hay: 'farm_obj_ph_hay', plaque: 'farm_obj_ph_plaque',
  farm_obj_fence_post: 'farm_obj_ph_fence_post', farm_obj_fence_rail: 'farm_obj_ph_fence_rail' };

const POST = 'farm_fence_post';     // the border's post and pickets (FarmWorld's fence art)

export class FarmObjectLayer {
  constructor(scene, world) {
    this.scene = scene;
    this.world = world;
    this._items = new Map();     // placement id → { obj, sig }
    this._pending = new Set();
  }

  refresh(data, nowMs = serverNow()) {
    if (!data?.catalog?.farm?.objects) return;
    const size = this.world.size;
    const seen = new Set();
    for (const d of drawList(data, nowMs)) {          // a tree's key follows its stage: a ripening redraws it
      seen.add(d.id);
      const p = (data.placements ?? []).find((q) => q.id === d.id);
      const spec = d.plaque ? data.catalog.farm.objects.plaque : data.catalog.farm.objects[p.item];
      const sig = JSON.stringify([d.front, d.flip, d.parts, d.key, this.scene.textures.exists(d.key), size]);
      const cur = this._items.get(d.id);
      if (cur?.sig === sig) continue;
      cur?.obj.destroy();
      const obj = d.parts ? this._fence(d, size) : this._single(d, spec, p, size);
      this.world.objects.add(obj);
      this._items.set(d.id, { obj, sig });
    }
    for (const [id, it] of this._items) if (!seen.has(id)) { it.obj.destroy(); this._items.delete(id); }
  }

  _texture(key, kind) {
    if (this.scene.textures.exists(key)) return key;
    if (!this._pending.has(key)) {
      this._pending.add(key);
      preloader.whenReady?.(key, () => this.refresh(this.world._objectData?.()));
    }
    return PH[key] ?? PH[kind] ?? PH.decoration;
  }

  _single(d, spec, p, size) {
    // The sprite's base: the middle of a tree or multi-cell decoration, else the front cell (objectAnchor).
    const a = objectAnchor(spec, p.x, p.y);
    const base = cellFToLocal(a.fx, a.fy, size);
    const c = { x: base.x, y: base.y + a.dy - CROP_BASE_Y };                     // "cell centre" for the offsets below
    const tex = this._texture(d.key, spec.kind);
    if (tex === d.key && !d.plaque) {
      const src = this.scene.textures.get(tex).getSourceImage();
      const f = floatParts(p.item, src.width, src.height);
      if (f) return this._floating(tex, f, base.x, base.y + a.dy, d, size);
    }
    const img = this.scene.add.image(base.x, base.y + a.dy, tex).setOrigin(0.5, 1).setFlipX(d.flip);
    if (tex === d.key) {
      const src = img.texture.getSourceImage();
      img.setDisplaySize(src.width * 0.5, src.height * 0.5);          // art is authored at 2×
    } else {
      const w = ((spec.w + spec.h) / 2) * TILE_W * 0.9;
      img.setDisplaySize(w, w * (spec.kind === 'landmark' ? 0.9 : 0.7));
    }
    const fc = cellCentre(d.front.x, d.front.y, size);                  // depth from the front cell, whatever the anchor
    img.setDepth(depthAt(fc.y + TILE_H / 2 + 1));                    // just in front of a crop on the same cell
    if (!d.plaque) return img;
    const box = this.scene.add.container(0, 0, [img]);
    // The crop's front-view icon painted on the board (the field art, seen from above, looked like
    // plants growing out of the sign). Falls back to nothing while the icon atlas loads.
    const frame = iconKey(`crop_${d.plaque}`);
    if (this.scene.textures.exists('icons') && this.scene.textures.get('icons').has(frame)) {
      const e = plaqueEmblem(img.displayWidth, img.displayHeight, d.flip);
      box.add(this.scene.add.image(c.x + e.dx, c.y + CROP_BASE_Y + e.dy, 'icons', frame).setDisplaySize(e.size, e.size));
    }
    box.setDepth(img.depth);
    return box;
  }

  // A decoration that moves (floatParts): its ground part stands still, its top sways and rises about the cut,
  // two frames of the same picture. Each placement starts at its own point of the swing (from its id), so two
  // bunches side by side don't move in step. Drawn without roundPixels: it snaps each picture's corner to a whole
  // pixel, so a turning picture jumped about a pixel every frame (owner: "shivering") and its foot left the bag's.
  _floating(tex, f, x, y, d, size) {
    const t = this.scene.textures.get(tex);
    for (const [name, r] of [['float_top', f.top], ['float_bottom', f.bottom]]) if (!t.has(name)) t.add(name, 0, r.x, r.y, r.w, r.h);
    const half = f.bottom.w * 0.25;                                     // half the bag's on-screen width
    x = Math.round(x - half) + half; y = Math.round(y);                // the still bag's edges land on whole pixels
    const bottom = this.scene.add.image(x, y, tex, 'float_bottom').setOrigin(0.5, 1).setFlipX(d.flip).setScale(0.5);   // art is authored at 2×
    const top = this.scene.add.image(x, y - f.bottom.h * 0.5, tex, 'float_top').setOrigin(0.5, f.pivotY).setFlipX(d.flip).setScale(0.5);
    const box = this.scene.add.container(0, 0, [top, bottom]);         // the bag over the top's lap
    const render = box.renderWebGL;
    box.renderWebGL = (renderer, src, camera, ...rest) => {           // roundPixels floors x/y, renderRoundPixels the quad
      const snap = [camera.roundPixels, camera.renderRoundPixels];
      camera.roundPixels = camera.renderRoundPixels = false;
      try { render.call(box, renderer, src, camera, ...rest); } finally { [camera.roundPixels, camera.renderRoundPixels] = snap; }
    };
    const fc = cellCentre(d.front.x, d.front.y, size);
    box.setDepth(depthAt(fc.y + TILE_H / 2 + 1));
    const phase = [...d.id].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % f.periodMs;
    const loop = { yoyo: true, repeat: -1, ease: 'Sine.easeInOut' };
    const tweens = [
      this.scene.tweens.add({ targets: top, angle: { from: -f.swayDeg, to: f.swayDeg }, duration: f.periodMs / 2, ...loop }),
      this.scene.tweens.add({ targets: top, scaleY: { from: 0.5, to: 0.5 * (1 + f.rise) }, duration: f.periodMs * 0.37, ...loop }),
    ];
    for (const tw of tweens) tw.seek(phase);
    box.once('destroy', () => { for (const tw of tweens) tw.remove(); });
    return box;
  }

  // A player fence cell (drawFenceCell), its joins read off the draw list's rail parts.
  _fence(d, size) {
    const joins = Object.fromEntries(['nx', 'px', 'ny', 'py'].map((k) => [k, d.parts.includes(`rail_${k}`)]));
    return drawFenceCell(this.scene, d.front.x, d.front.y, joins, size);
  }

  /** The placement id whose picture is drawn at this world point (its actual pixels, front-most), among
   *  those `accept(id)` allows — so hovering a hut's roof finds the hut (owner 2026-10-01), not the cell behind. */
  idAtScreen(x, y, accept = () => true) {
    let best = null;
    for (const [id, it] of this._items) {
      if (!accept(id)) continue;
      // A plaque's board and emblem, or a floating decoration's two parts: any of its pictures counts.
      const depth = it.obj.depth;
      for (const img of (it.obj.type === 'Container' ? it.obj.list : [it.obj])) {
        if (!img?.visible || !img.getBounds) continue;
        const b = img.getBounds();
        if (!b.contains(x, y)) continue;
        let u = (x - b.x) / b.width; const v = (y - b.y) / b.height;
        if (img.flipX) u = 1 - u;
        const a = img.frame ? this.scene.textures.getPixelAlpha(Math.floor(u * img.frame.width), Math.floor(v * img.frame.height), img.texture.key, img.frame.name) : 255;
        if ((a ?? 255) <= 40) continue;
        if (!best || depth > best.depth) best = { id, depth };
        break;
      }
    }
    return best?.id ?? null;
  }

  destroy() { for (const it of this._items.values()) it.obj.destroy(); this._items.clear(); }
}

// One fence cell, shared by the built fence and the placement ghost: a border post on the cell's
// centre only where the run ends or turns (fencePostNeeded), then one border segment on each edge
// shared with a -x / -y neighbour, pinned like the border's (anchorFor) at the middle between the
// two cells. The post goes first so the segment covers it; the neighbour behind sorts earlier, so
// its post is covered too.
export function drawFenceCell(scene, x, y, joins, size) {
  const c = cellCentre(x, y, size);
  const box = scene.add.container(0, 0);
  const art = (key, px, py, anchor) => {
    const img = scene.add.image(px, py, key).setOrigin(anchor[0], anchor[1]);
    const src = img.texture.getSourceImage();
    return img.setDisplaySize(src.width * 0.5, src.height * 0.5);        // scenery is authored at 2×
  };
  if (fencePostNeeded(joins)) {
    box.add(scene.textures.exists(POST)
      ? art(POST, c.x, c.y, [0.5, 1])
      : scene.add.image(c.x, c.y, 'farm_obj_ph_fence_post').setOrigin(0.5, 1).setDisplaySize(10, 30));
  }
  for (const seg of fenceSegments(x, y, joins)) {
    if (!scene.textures.exists(seg.key)) continue;
    const m = cellFToLocal(seg.fx, seg.fy, size);
    box.add(art(seg.key, m.x, m.y, anchorFor(seg.key)));
  }
  box.setDepth(depthAt(c.y + TILE_H / 2 + 1));
  return box;
}
